import React, { useEffect } from "react";
import { Router } from "@reach/router";
import {
  Footer,
  ButtonProps,
  NavigationHeader,
  useFeatureContext,
} from "@world-vision/wv360-core-library";
import { useStaticQuery, graphql, navigate } from "gatsby";
import { GATSBY_AUTHENTICATED_URL } from 'gatsby-env-variables';

import Story from "../Story/Story";
import { AemContentFragment, navigationConfig, resolveNavigation } from "../../utility/navigation";
import { useLocation } from "@reach/router";
import usePageTracker from '../../utility/hooks/analytics/usePageTracker';
import useVideoTracker from "../../utility/hooks/analytics/useVideoTracker";
import { CommonPageProps } from "../../types/components/props";

export interface NavigationItem {
  name: string;
  fragmentId: string;
  path: string;
  navigable: boolean;
  children: NavigationItem[];
}

export type NavigationData = {
  navigation: NavigationItem[];
};

const query = graphql`
  {
    site {
      siteMetadata {
        footer {
          backgroundColor
          color
          Content {
            text
          }
          contactInfo
          contactPageURL
          socialButtons {
            name
            size
            color
            rounded
            backgroundColor
            backgroundSize
            link {
              href
              target
            }
          }
          links {
            text
            href
          }
          ImageIcon {
            name
            alt
            iconWidth
            iconHeight
            height
            width
            link {
              href
              target
            }
          }
          imageColor
          ImgContent
        }
      }
    }
    datasource {
      containerList {
        items {
          navigationTitle
          _path
        }
      }
    }
  }
`;

export type FooterData = {
  backgroundColor: string;
  color: string;
  Content: {
    text: string;
  }[];
  contactInfo: string;
  contactPageURL: string;
  socialButtons: {
    name: string;
    size: string;
    color: string;
    rounded: boolean;
    backgroundColor: string;
    backgroundSize: string;
    link: {
      href: string;
      target: string;
    };
  }[];
  links: {
    text: string;
    href?: string;
  }[];
  ImageIcon: {
    name: string;
    alt: string;
    iconWidth: string;
    iconHeight: string;
    height?: string;
    width?: string;
    link: {
      href: string;
      target: string;
    };
  }[];
  imageColor: string;
  ImgContent: string;
}

export type LayoutQueryType = {
  site: {
    siteMetadata: {
      footer: FooterData;
    }
  }
  datasource: {
    containerList: {
      items: AemContentFragment[];
    }
  }
}

export type LayoutProps = {
  /**
   * The children components to be rendered
   */
  children: React.ReactNode;
  /**
   * The class name for the header component
   */
  headerClassName?: string;
  /**
   * is footer need to be visible or not
   * @default true
   */
  showFooter?: boolean;
  /**
   * is navigation header need to be visible or not
   * @default true
   */
  showHeader?: boolean;
} & Partial<CommonPageProps>;

/** 
 * Layout component - This component is used to render the header and children components
 * 
 * @param {LayoutProps} props - The props of the layout component
 * @returns {React.ReactNode} - The layout component
 * 
 */
const Layout: React.FC<LayoutProps> = ({
  children,
  headerClassName,
  showFooter = true,
  showHeader = true,
}) => {
  const {
    site: {
      siteMetadata: { footer = {} as FooterData },
    },
    datasource: {
      containerList: { items = [] },
    },
  } = useStaticQuery<LayoutQueryType>(query);

  usePageTracker();
  useVideoTracker();
  const featureContext = useFeatureContext();

  const lang = "en";

  const onLogoClick = () => {
    navigate(`/${lang}`);
  };

  const onNavLinkClick = (
    path: string,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    const navPath = path.startsWith("/") ? path : `/${path}`;
    const combinedPath = window.location.origin + navPath;
    navigate(combinedPath);
  };

  const navToSignUp = () => navigate(`${GATSBY_AUTHENTICATED_URL}/register`);
  const buttonData: Array<ButtonProps> = [
    {
      text: "Sign Up",
      mode: "primary",
      size: "sm",
      onClick: navToSignUp,
    },
  ];

  const footerProps = {
    ...footer,
    Content: footer?.Content?.map((content) => ({
      text: content.text,
    })) ?? [],
  };

  const resolvedNavigation = resolveNavigation(
    navigationConfig,
    items
  );

  const location = useLocation();

  useEffect(() => {
    console.log("Page loaded:", window.location.href);
    setTimeout(() => window.scrollTo(0, 0), 1);
  }, [location.pathname]);

  useEffect(() => {
    console.log("Feature Flag context loaded:", featureContext);
  }, [featureContext]);

  // TODO: Need to Fix the following, Temp fix for navigation unwnated highlight at first time load
  const isHomePage = location.pathname.replace(`/${lang}/`, "").replace(`/${lang}`, "").replace(/\/$/, "");
  const activePath = isHomePage === "" ? location?.pathname : undefined;

  return (
    <>
      <Router>
        <Story path={`/${lang}/stories/*`} />
      </Router>
      {showHeader && (
        <NavigationHeader
          className={headerClassName}
          logoAltText="wvc-logo"
          onLogoClick={onLogoClick}
          navLinks={{ navigation: resolvedNavigation }}
          buttonData={buttonData}
          onNavLinkClick={onNavLinkClick}
          activePath={activePath}
        />
      )}
      {children}
      {showFooter && <Footer {...footerProps} />}
    </>
  );
};

export default Layout;